import { inject, Injectable } from '@angular/core';
import { from, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import {
  ignoreModalConfirm,
  ignoreModalError,
  ModalService,
  NotificationService,
  OkModalComponent,
  ShipperResponse,
  YesNoModalComponent,
} from 'tms-lib';
import { ShipperInterfaceHeaderService } from '../edit-create/sales/shipper-interface-header.service';

/**
 * A shipment's shipper changing (HFW testing points Oct 2026, point 2, D-1): the ONE place the Details
 * tabs (despatch note, sales manifest, purchase order despatch) and the Shipping Interface ask before
 * the packs set up for the old shipper are deleted, and say why a booked shipment keeps its shipper.
 */
@Injectable({
  providedIn: 'root',
})
export class ShipperChangeService {
  private readonly shipperInterfaceHeaderService = inject(ShipperInterfaceHeaderService);
  private readonly modalService = inject(ModalService);
  private readonly notificationService = inject(NotificationService);

  /**
   * Changes a document's shipper through ChangeShipper. When the shipment has packs the API changes
   * nothing and asks first; a Yes sends the change again with the deletion confirmed. Emits true once
   * the shipper has changed, and false when it has not - the user said No, the shipment is booked, or
   * the API refused for another reason (shown here). The caller then puts the old shipper back.
   */
  changeShipper(shipperId: string, documentId: string): Observable<boolean> {
    return this.shipperInterfaceHeaderService.changeShipper(shipperId, documentId).pipe(
      switchMap((response: ShipperResponse) => {
        if (!response?.packDeleteConfirmationRequired) {
          return of(this.changed(response));
        }

        return from(this.confirmPackDelete(response.packCount, response.currentShipperName)).pipe(
          switchMap((yes: boolean) =>
            yes
              ? this.shipperInterfaceHeaderService
                  .changeShipper(shipperId, documentId, true)
                  .pipe(map((confirmed: ShipperResponse) => this.changed(confirmed)))
              : of(false),
          ),
        );
      }),
      // A failed request (a 500, the network) changed nothing either, so the caller must put the old
      // shipper back - left on screen, a later Save of the Details tab would write it past these checks.
      catchError((error: unknown) => {
        this.notificationService.apiFail(error, $localize`Change Shipper failed`);
        return of(false);
      }),
    );
  }

  /** Asks before the packs set up for the current shipper are deleted (AC-8). Resolves true on Yes. */
  confirmPackDelete(packCount: number, shipperName: string): Promise<boolean> {
    const { modal, component } = this.modalService.openSmall<YesNoModalComponent>(YesNoModalComponent);
    component.header.set($localize`Change Shipper`);
    // The count is not worded as a plural: this text is built in TypeScript, where $localize has no ICU
    // support, and the Yes/No window takes a string (ui.md, On-page messages).
    component.message.set(
      $localize`Changing the shipper will delete the packs set up for '${shipperName}'. Packs to delete: ${packCount}. Do you want to continue?`,
    );
    return modal.result.then(
      (yes: boolean) => !!yes,
      () => false,
    );
  }

  /** A booked shipment keeps its shipper (AC-10). */
  refuseBooked(shipperName: string) {
    this.showMessage(
      $localize`This shipment has already been booked with '${shipperName}', so the shipper cannot be changed.`,
    );
  }

  // ChangeShipper answers 200 with an errorMessage in the body (never an error status), so the HTTP
  // interceptor never sees it - it is shown here or it vanishes. The API changes nothing when it refuses.
  private changed(response: ShipperResponse): boolean {
    if (response?.errorMessage) {
      this.showMessage(response.errorMessage);
      return false;
    }
    return true;
  }

  private showMessage(message: string) {
    const { modal, component } = this.modalService.openSmall<OkModalComponent>(OkModalComponent);
    component.header.set($localize`Change Shipper`);
    component.message.set(message);
    modal.result.then(ignoreModalConfirm, ignoreModalError);
  }
}
